import { useEffect, useState } from "react";
import axios from "axios";
import { Droplets, Pencil, Save } from "lucide-react";
import { toast } from "sonner";
import { InformationalStatusText } from "@/components/InformationalStatusText";
import { TypedDateInput } from "@/components/TypedDateInput";
import { formatBusinessDate } from "@/utils/datetime";
import { getProjectModuleAvailability } from "@/utils/projectFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const createDraft = (project) => ({
  water_locate_required: Boolean(project.water_locate_required),
  water_locate_created_date: project.water_locate_created_date || "",
  water_locate_submitted_at: project.water_locate_submitted_at || "",
  water_locate_expires_on: project.water_locate_expires_on || "",
});

const displayDate = (value) => (value ? formatBusinessDate(value) : "Not recorded");

export const ProjectWaterLocatePanel = ({
  canManageRecords,
  canSubmitWaterLocate,
  onSaved,
  project,
  features = {},
}) => {
  const [draft, setDraft] = useState(() => createDraft(project));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(createDraft(project));
    setEditing(false);
  }, [project]);

  const water = project.readiness?.water || {};
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = canManageRecords
        ? draft
        : { water_locate_submitted_at: draft.water_locate_submitted_at };
      await axios.patch(`${API}/projects/${project.id}`, payload);
      toast.success("Water Locate saved.");
      await onSaved();
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Water Locate could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(createDraft(project));
    setEditing(false);
  };

  if (!getProjectModuleAvailability(features).locates) return null;

  return (
    <article
      className="linked-record-panel project-water-locate-panel"
      data-testid="project-water-locate-panel"
    >
      <div className="project-water-locate-heading">
        <PanelTitle />
        {
          (canManageRecords || (canSubmitWaterLocate && project.water_locate_required)) &&
            !editing && (
          <button
            className="linked-record-workspace-link"
            data-testid="project-water-locate-edit-button"
            onClick={() => setEditing(true)}
            type="button"
          >
            <Pencil size={14} />
            Edit
          </button>
        )}
      </div>
      {!editing && (
        <div className="project-water-locate-summary">
          <div data-testid="project-water-locate-required">
            <span>Required</span>
            <strong>{project.water_locate_required ? "Yes" : "No"}</strong>
          </div>
          <div data-testid="project-water-locate-created-date">
            <span>Date created</span>
            <strong>{displayDate(project.water_locate_created_date)}</strong>
          </div>
          <div data-testid="project-water-locate-submitted-date">
            <span>Submitted</span>
            <strong>{displayDate(project.water_locate_submitted_at)}</strong>
          </div>
          <div data-testid="project-water-locate-ready-date">
            <span>Ready</span>
            <strong>{displayDate(project.water_locate_ready_at)}</strong>
          </div>
          <div data-testid="project-water-locate-expiration-date">
            <span>Expires</span>
            <strong>{displayDate(project.water_locate_expires_on)}</strong>
          </div>
          <InformationalStatusText
            label={water.reason || "Not required"}
            testId="project-water-locate-status"
          />
        </div>
      )}
      {editing && (
        <form
          className="project-water-locate-form"
          data-testid="project-water-locate-form"
          onSubmit={save}
        >
          {canManageRecords && (
            <label
              className="project-record-check"
              htmlFor="project-water-locate-required-input"
            >
              <input
                checked={draft.water_locate_required}
                data-testid="project-water-locate-required-input"
                id="project-water-locate-required-input"
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    water_locate_required: event.target.checked,
                  }));
                }}
                type="checkbox"
              />
              <span>Water Locate required</span>
            </label>
          )}
          {canManageRecords && draft.water_locate_required && (
            <>
              <label htmlFor="project-water-locate-created-date-input">
                Date created
                <TypedDateInput
                  data-testid="project-water-locate-created-date-input"
                  id="project-water-locate-created-date-input"
                  onChange={(value) => {
                    setDraft((current) => ({
                      ...current,
                      water_locate_created_date: value,
                    }));
                  }}
                  value={draft.water_locate_created_date}
                />
              </label>
            </>
          )}
          {canSubmitWaterLocate && project.water_locate_required && (
            <label htmlFor="project-water-locate-submitted-date-input">
              Submitted
              <TypedDateInput
                data-testid="project-water-locate-submitted-date-input"
                id="project-water-locate-submitted-date-input"
                onChange={(value) => {
                  setDraft((current) => ({
                    ...current,
                    water_locate_submitted_at: value,
                  }));
                }}
                value={draft.water_locate_submitted_at}
              />
            </label>
          )}
          {canManageRecords && draft.water_locate_required && (
            <label htmlFor="project-water-locate-expiration-date-input">
                Expiration date
              <TypedDateInput
                  data-testid="project-water-locate-expiration-date-input"
                  id="project-water-locate-expiration-date-input"
                onChange={(value) => {
                    setDraft((current) => ({
                      ...current,
                    water_locate_expires_on: value,
                    }));
                  }}
                  value={draft.water_locate_expires_on}
                />
            </label>
          )}
          <div className="project-water-locate-actions">
            <button
              className="board-refresh-button"
              data-testid="project-water-locate-cancel-button"
              disabled={saving}
              onClick={cancel}
              type="button"
            >
              Cancel
            </button>
            <button
              className="invoice-primary-button"
              data-testid="project-water-locate-save-button"
              disabled={saving}
              type="submit"
            >
              <Save size={14} />
              {saving ? "Saving…" : "Save Water Locate"}
            </button>
          </div>
        </form>
      )}
    </article>
  );
};

function PanelTitle() {
  return (
    <div className="project-panel-heading">
      <Droplets size={17} />
      <div>
        <span className="operations-kicker">Linked record</span>
        <h2>Water Locate</h2>
      </div>
    </div>
  );
}
